import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AutoComplete, Button, Col, DatePicker, Form, Input, InputNumber, Radio, Row,
  Select, Tag, TreeSelect, Typography, Upload, message
} from 'antd';
import { DeleteOutlined, DownloadOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import { buildBudgetOrgTreeSelectProps } from '../../utils/orgTree';
import { buildExpenseClaimSubject } from '../../utils/expenseClaimSubject';
import {
  createPayeeFieldValidator, validateChineseIdCard, validateSupplierTaxNo
} from '../../utils/expensePayeeFieldValidate';
import { amountToChinese } from '../../utils/moneyChinese';
import {
  LOAN_DOC_TYPE,
  PREPAYMENT_DOC_TYPE,
  PREPAYMENT_DOC_TYPE_OPTIONS,
  prepaymentAddLineButtonLabel,
  prepaymentAmountLabel,
  prepaymentLineSectionTitle,
  prepaymentReasonPlaceholder,
  prepaymentTotalLabel,
  resolvePrepaymentBizWord
} from '../../utils/prepaymentDocTypeLabels';
import './ExpenseClaimForm.css';

const { Text, Link } = Typography;
const PAYEE_TYPE_OPTIONS = [
  { value: 'external_person', label: '外部个人' },
  { value: 'supplier', label: '供应商' }
];
const DEFAULT_PAYEE_TYPE = 'external_person';
const URGENCY_OPTIONS = [{ value: 'normal', label: '正常' }, { value: 'urgent', label: '紧急' }];
export const MULTI_DEPT_BUDGET_ORG_CODE = '__MULTI_DEPT__';
const COL3 = 8; const COL2 = 12; const FULL = 24; const GUTTER = [16, 8];

const isMultiDept = (line) => String(line?.budget_org_code || '').trim() === MULTI_DEPT_BUDGET_ORG_CODE;
const emptySplit = () => ({ org_code: undefined, amount: undefined });
const round2 = (v) => Math.round(Number(v || 0) * 100) / 100;
const findOrgName = (orgs, code) => orgs.find((o) => o.org_code === code)?.org_name || code || '';
const defaultExpectedInvoiceDate = () => dayjs().add(1, 'month');

function monthBounds() {
  const ref = dayjs();
  return { min: ref.subtract(1, 'year').month(11).startOf('month'), max: ref.endOf('month') };
}

function validateOccurrenceMonth(value) {
  if (!value || !dayjs(value).isValid()) return null;
  const { min, max } = monthBounds();
  if (dayjs(value).isBefore(min, 'month') || dayjs(value).isAfter(max, 'month')) {
    return `费用产生时间须在上年度12月至本月之间（${min.format('YYYY-MM')} 至 ${max.format('YYYY-MM')}）`;
  }
  return null;
}

function periodLabel(periodType, yearMonth) {
  const text = dayjs(yearMonth).isValid() ? dayjs(yearMonth).format('YYYY-MM') : String(yearMonth || '').slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(text)) return '';
  const [y, m] = [Number(text.slice(0, 4)), Number(text.slice(5, 7))];
  if (periodType === 'annual') return `${y}年度`;
  if (periodType === 'quarterly') return `${y}年第${Math.ceil(m / 3)}季度`;
  return `${y}年${m}月`;
}

function FieldCol({ span = COL3, children }) {
  return <Col xs={24} md={span}>{children}</Col>;
}

function Section({ title, children }) {
  return (
    <section className="expense-claim-section">
      <div className="expense-claim-section-title">{title}</div>
      {children}
    </section>
  );
}

function budgetSplitRule(lineIndex, bizWord = '预付款') {
  return ({ getFieldValue }) => ({
    validator: async () => {
      const line = getFieldValue(['lines', lineIndex]) || {};
      if (!isMultiDept(line)) return;
      const splits = line.budget_splits || [];
      if (splits.length < 2) throw new Error('多部门分摊至少添加两个部门');
      const codes = splits.map((s) => String(s?.org_code || '').trim());
      if (codes.some((c) => !c) || new Set(codes).size !== codes.length) {
        throw new Error('请为每个分摊项选择不重复的部门');
      }
      const total = round2(line.prepay_amount);
      if (Number.isFinite(total) && total > 0) {
        const sum = round2(splits.reduce((acc, s) => acc + Number(s?.amount || 0), 0));
        if (Math.abs(sum - total) > 0.009) {
          throw new Error(`分摊金额之和须等于${bizWord}金额`);
        }
      }
    }
  });
}

async function checkBudget(lines, categoryId, monthValue, docType) {
  const amountLabel = prepaymentAmountLabel(docType);
  if (!categoryId || !monthValue) return;
  const month = monthValue?.format?.('YYYY-MM') ?? String(monthValue).slice(0, 7);
  const items = [];
  (lines || []).forEach((line, i) => {
    if (isMultiDept(line)) {
      (line.budget_splits || []).forEach((s, j) => {
        const code = String(s?.org_code || '').trim();
        const amt = Number(s?.amount || 0);
        if (code && amt > 0) items.push({ lineNo: i + 1, splitNo: j + 1, code, amt });
      });
    } else {
      const code = String(line?.budget_org_code || '').trim();
      const amt = Number(line?.prepay_amount || 0);
      if (code && code !== MULTI_DEPT_BUDGET_ORG_CODE && amt > 0) items.push({ lineNo: i + 1, code, amt });
    }
  });
  for (const item of items) {
    const res = await api.get('/expense/exp-prepayment/budget-balance', {
      params: { org_code: item.code, category_id: categoryId, expense_occurrence_month: month }
    });
    if (!res.data?.success) continue;
    const avail = Number(res.data.data?.amount_available || 0);
    if (item.amt > avail + 0.009) {
      const prefix = item.splitNo ? `第 ${item.lineNo} 行第 ${item.splitNo} 个分摊部门` : `第 ${item.lineNo} 行`;
      throw new Error(`${prefix}${amountLabel}超过剩余预算（剩余 ¥${avail.toFixed(2)}）`);
    }
  }
}

function LineBudgetBalance({ orgCode, categoryId, expenseOccurrenceMonth, lineAmount }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!orgCode || !categoryId || !expenseOccurrenceMonth) { setBalance(null); return undefined; }
    const month = expenseOccurrenceMonth?.format?.('YYYY-MM') ?? String(expenseOccurrenceMonth).slice(0, 7);
    let cancelled = false;
    setLoading(true);
    api.get('/expense/exp-prepayment/budget-balance', {
      params: { org_code: orgCode, category_id: categoryId, expense_occurrence_month: month }
    }).then((res) => { if (!cancelled && res.data.success) setBalance(res.data.data); })
      .catch(() => { if (!cancelled) setBalance(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgCode, categoryId, expenseOccurrenceMonth]);
  if (!orgCode || !categoryId || !expenseOccurrenceMonth) return null;
  if (loading) return <Text type="secondary" className="expense-line-budget-hint">预算查询中…</Text>;
  if (!balance) return null;
  const remaining = Number(balance.amount_available || 0);
  const insufficient = Number(lineAmount || 0) > 0 && round2(remaining - lineAmount) < -0.009;
  const label = `剩余预算 ¥${remaining.toFixed(2)}`;
  return <Tag color={insufficient ? 'error' : 'processing'} className="expense-line-budget-tag" title={label}>{label}</Tag>;
}

function SplitRow({ splitField, lineIdx, splitIdx, canRemove, onRemove, treeProps, categoryId, month, form }) {
  const orgCode = Form.useWatch(['lines', lineIdx, 'budget_splits', splitField.name, 'org_code'], form);
  const amount = Form.useWatch(['lines', lineIdx, 'budget_splits', splitField.name, 'amount'], form);
  return (
    <div className="expense-line-split-row">
      <Form.Item name={[splitField.name, 'org_code']} label={splitIdx === 0 ? '分摊部门' : undefined}
        rules={[{ required: true, message: '请选择分摊部门' }]} className="expense-line-split-org">
        <TreeSelect {...treeProps} placeholder="选择部门" />
      </Form.Item>
      <Form.Item name={[splitField.name, 'amount']} label={splitIdx === 0 ? '分摊金额' : undefined}
        rules={[{ required: true, message: '请填写分摊金额' }]} className="expense-line-split-amount">
        <InputNumber min={0.01} precision={2} placeholder="0.00" style={{ width: '100%' }} />
      </Form.Item>
      <div className="expense-line-split-budget">
        <LineBudgetBalance orgCode={orgCode} categoryId={categoryId} expenseOccurrenceMonth={month} lineAmount={amount} />
      </div>
      {canRemove ? (
        <Button type="text" size="small" danger icon={<DeleteOutlined />} className="expense-line-split-remove" onClick={onRemove} />
      ) : <span className="expense-line-split-remove-placeholder" />}
    </div>
  );
}

function LineCard({
  field, index, form, canRemove, onRemove, category, treeProps, splitTreeProps, categoryId, month,
  lineContentDisabled = false,
  subitemDisabled = false
}) {
  const docType = Form.useWatch('doc_type', form);
  const bizWord = resolvePrepaymentBizWord(docType);
  const amountLabel = prepaymentAmountLabel(docType);
  const line = (Form.useWatch('lines', form) || [])[field.name] || {};
  const multi = isMultiDept(line);
  return (
    <div className="expense-line-card">
      <div className="expense-line-card-header">
        <Text strong className="expense-line-card-title">{prepaymentLineSectionTitle(docType)} {index + 1}</Text>
        {canRemove && !lineContentDisabled && (
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={onRemove}>删除</Button>
        )}
      </div>
      <div className="expense-line-row expense-line-row-budget">
        <Form.Item name={[field.name, 'budget_org_code']} label="费用归属部门"
          rules={[{ required: true, message: '请选择费用归属部门' }]} className="expense-line-field">
          <TreeSelect {...treeProps} placeholder="选择部门" disabled={lineContentDisabled} onChange={(value) => {
            const lines = [...(form.getFieldValue('lines') || [])];
            const next = { ...(lines[field.name] || {}) };
            next.budget_splits = value === MULTI_DEPT_BUDGET_ORG_CODE ? [emptySplit(), emptySplit()] : undefined;
            lines[field.name] = next;
            form.setFieldsValue({ lines });
          }} />
        </Form.Item>
        <Form.Item name={[field.name, 'subitem_id']} label="预算二级科目"
          rules={category?.sub_items?.length ? [{ required: true, message: '请选择二级科目' }] : []}
          className="expense-line-field">
          <Select allowClear disabled={subitemDisabled || !category?.sub_items?.length}
            placeholder={category?.sub_items?.length ? '选择二级科目' : '无二级科目'} showSearch optionFilterProp="label"
            options={(category?.sub_items || []).map((s) => ({ value: s.id, label: s.name }))} />
        </Form.Item>
        <Form.Item name={[field.name, 'expense_detail']} label="费用明细"
          rules={[{ required: true, message: '请填写费用明细' }]} className="expense-line-field">
          <Input placeholder="费用说明" disabled={lineContentDisabled} />
        </Form.Item>
      </div>
      {multi && (
        <div className="expense-line-splits">
          <Text type="secondary" className="expense-line-splits-title">{`分摊明细（至少 2 个部门，金额之和须等于${bizWord}金额）`}</Text>
          <Form.List name={[field.name, 'budget_splits']}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((sf, si) => (
                  <SplitRow key={sf.key} splitField={sf} lineIdx={field.name} splitIdx={si}
                    canRemove={fields.length > 2} onRemove={() => remove(sf.name)}
                    treeProps={splitTreeProps} categoryId={categoryId} month={month} form={form} />
                ))}
                <div className="expense-line-splits-actions">
                  <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => add(emptySplit())}>添加分摊部门</Button>
                </div>
              </>
            )}
          </Form.List>
        </div>
      )}
      <div className="expense-line-row">
        <Form.Item label={amountLabel} required className="expense-line-field expense-line-actual-wrap">
          <div className="expense-line-actual-input-row">
            <Form.Item name={[field.name, 'prepay_amount']} noStyle rules={[
              { required: true, message: `请填写${amountLabel}` },
              ({ getFieldValue }) => ({
                validator: async (_, v) => {
                  if (v == null || v === '') return;
                  const cur = getFieldValue(['lines', field.name]) || {};
                  if (isMultiDept(cur)) {
                    await budgetSplitRule(field.name, bizWord)({ getFieldValue }).validator();
                  }
                }
              })
            ]}>
              <InputNumber min={0.01} precision={2} placeholder="0.00" disabled={lineContentDisabled} />
            </Form.Item>
            <div className="expense-line-budget-slot">
              {!multi && (
                <LineBudgetBalance orgCode={line.budget_org_code} categoryId={categoryId}
                  expenseOccurrenceMonth={month} lineAmount={line.prepay_amount} />
              )}
            </div>
          </div>
        </Form.Item>
      </div>
    </div>
  );
}

async function assertPrepaymentFormValid(form) {
  try {
    await form.validateFields();
  } catch (err) {
    if (err?.errorFields?.length) {
      throw new Error(err.errorFields[0]?.errors?.[0] || '请完善表单');
    }
    throw err;
  }
}

export function applyMatInboundPrepaymentPrefill(form, prefill, meta) {
  if (!prefill || typeof prefill !== 'object') return;
  const budgetOrg = prefill.budget_org_code || prefill.applicant_dept_code;
  const monthText = prefill.expense_occurrence_month
    ? String(prefill.expense_occurrence_month).slice(0, 7)
    : '';
  const lockedSubitemId = prefill.budget_subject_locked ? prefill.subitem_id : undefined;
  form.setFieldsValue({
    doc_type: PREPAYMENT_DOC_TYPE,
    reason: prefill.reason,
    expense_occurrence_month: monthText ? dayjs(`${monthText}-01`) : undefined,
    payee_type: 'supplier',
    payee_ref: prefill.payee_id,
    payee_name: prefill.payee_name,
    bank_name: prefill.bank_name,
    bank_branch_name: prefill.bank_branch_name,
    bank_account_no: prefill.bank_account_no,
    tax_no: prefill.tax_no,
    applicant_dept_code: prefill.applicant_dept_code || budgetOrg,
    category_id: prefill.budget_subject_locked ? prefill.category_id : undefined,
    related_approval_no: prefill.related_approval_no,
    related_approval_biz_type: prefill.related_approval_biz_type,
    related_approval_id: prefill.related_approval_id,
    related_inbound_ids: prefill.related_inbound_ids,
    expense_company_id: meta?.default_expense_company_id,
    urgency: 'normal',
    expected_invoice_date: defaultExpectedInvoiceDate(),
    no_invoice_commitment_files: [],
    other_attachments: [],
    lines: (prefill.lines || []).map((line) => ({
      expense_detail: line.expense_detail,
      budget_org_code: line.budget_org_code || budgetOrg,
      subitem_id: line.subitem_id ?? lockedSubitemId,
      prepay_amount: line.prepay_amount
    })),
    remark: ''
  });
}

export async function buildPrepaymentPayload(form, submit) {
  if (submit) await assertPrepaymentFormValid(form);
  const values = form.getFieldsValue(true);
  const lines = (values.lines || []).map((line) => ({
    expense_detail: line.expense_detail,
    budget_org_code: line.budget_org_code,
    budget_splits: isMultiDept(line)
      ? (line.budget_splits || []).map((s) => ({ org_code: s.org_code, org_name: s.org_name, amount: s.amount }))
      : undefined,
    subitem_id: line.subitem_id || null,
    prepay_amount: line.prepay_amount
  }));
  if (submit) {
    const bizWord = resolvePrepaymentBizWord(values.doc_type);
    for (let i = 0; i < lines.length; i += 1) {
      if (isMultiDept(lines[i])) {
        await budgetSplitRule(i, bizWord)({ getFieldValue: form.getFieldValue.bind(form) }).validator();
      }
    }
    await checkBudget(lines, values.category_id, values.expense_occurrence_month, values.doc_type);
    if (!values.no_invoice_commitment_files?.length) throw new Error('请上传无票承诺书');
  }
  return {
    submit,
    expense_company_id: values.expense_company_id,
    applicant_dept_code: values.applicant_dept_code,
    category_id: values.category_id,
    expense_occurrence_month: values.expense_occurrence_month?.format?.('YYYY-MM') ?? values.expense_occurrence_month ?? null,
    budget_subject: values.category_id ? `${values.category_id}::` : undefined,
    reason: values.reason,
    urgency: values.urgency,
    expected_invoice_date: values.expected_invoice_date?.format?.('YYYY-MM-DD') ?? values.expected_invoice_date ?? null,
    payee_type: values.payee_type,
    employee_no: values.payee_type === 'employee' ? values.payee_ref : undefined,
    payee_id: values.payee_type !== 'employee' && values.payee_ref ? values.payee_ref : undefined,
    payee_name: values.payee_name,
    bank_name: values.bank_name,
    bank_branch_name: values.bank_branch_name,
    bank_account_no: values.bank_account_no,
    id_card_no: values.id_card_no,
    tax_no: values.tax_no,
    no_invoice_commitment_files: values.no_invoice_commitment_files || [],
    lines,
    other_attachments: values.other_attachments || [],
    remark: values.remark,
    related_approval_biz_type: values.related_approval_biz_type || undefined,
    related_approval_id: values.related_approval_id || undefined,
    related_approval_no: String(values.related_approval_no || '').trim() || undefined,
    related_inbound_ids: Array.isArray(values.related_inbound_ids)
      ? values.related_inbound_ids.filter(Boolean)
      : undefined
  };
}

export default function PrepaymentForm({ form, variant, variantConfig }) {
  const isFromMatInbound = variant === 'from_mat_inbound';
  const lockSourceFields = isFromMatInbound;
  const lockBudgetSubject = isFromMatInbound && Boolean(variantConfig?.prefill?.budget_subject_locked);
  const lockLineContent = isFromMatInbound;
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [meta, setMeta] = useState(null);
  const [payeeOptions, setPayeeOptions] = useState([]);
  const [payeeSearching, setPayeeSearching] = useState(false);
  const [bankOptions, setBankOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [commitmentUploading, setCommitmentUploading] = useState(false);
  const payeeSeq = useRef(0);
  const prevPayeeType = useRef(null);
  const prevDocType = useRef(null);
  const skipPayeeChangeRef = useRef(false);
  const matInboundFormInitRef = useRef(false);

  const docType = Form.useWatch('doc_type', form);
  const isLoanApplication = docType === LOAN_DOC_TYPE;
  const payeeType = Form.useWatch('payee_type', form);
  const payeeRef = Form.useWatch('payee_ref', form);
  const payeeName = Form.useWatch('payee_name', form);
  const payeeLocked = !isLoanApplication && Boolean(payeeRef) && !isFromMatInbound;
  const payeeFieldsDisabled = isLoanApplication || payeeLocked;
  const bankName = Form.useWatch('bank_name', form);
  const applicantDept = Form.useWatch('applicant_dept_code', form);
  const categoryId = Form.useWatch('category_id', form);
  const month = Form.useWatch('expense_occurrence_month', form);
  const reason = Form.useWatch('reason', form);
  const lines = Form.useWatch('lines', form) || [];
  const otherAttachments = Form.useWatch('other_attachments', form) || [];
  const commitmentFiles = Form.useWatch('no_invoice_commitment_files', form) || [];

  const splitTreeProps = useMemo(
    () => buildBudgetOrgTreeSelectProps(meta?.organizations || [], { style: { width: '100%' } }),
    [meta?.organizations]
  );
  const treeProps = useMemo(() => ({
    ...splitTreeProps,
    treeData: [...(splitTreeProps.treeData || []), {
      title: '多部门分摊', value: MULTI_DEPT_BUDGET_ORG_CODE, key: MULTI_DEPT_BUDGET_ORG_CODE, isLeaf: true
    }]
  }), [splitTreeProps]);
  const category = useMemo(() => (meta?.categories || []).find((c) => c.id === categoryId), [meta?.categories, categoryId]);
  const prepayTotal = useMemo(() => round2(lines.reduce((s, l) => s + Number(l?.prepay_amount || 0), 0)), [lines]);
  const deptName = useMemo(() => {
    const hit = (meta?.applicant_dept_options || []).find((d) => d.org_code === applicantDept);
    return hit?.org_name || findOrgName(meta?.organizations || [], applicantDept);
  }, [meta, applicantDept]);
  const bizWord = resolvePrepaymentBizWord(docType);
  const subject = useMemo(() => buildExpenseClaimSubject({
    applicantDeptName: deptName, applicantName: meta?.applicant_name || '',
    expenseOccurrenceMonth: month?.format?.('YYYY-MM') ?? month, reason
  }).replace(/报销/g, bizWord), [deptName, meta, month, reason, bizWord]);
  const payees = useMemo(
    () => payeeOptions.filter((p) => p.payee_type === (payeeType || DEFAULT_PAYEE_TYPE)),
    [payeeOptions, payeeType]
  );

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post('/expense/exp-prepayment/upload-attachment', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (!res.data.success) throw new Error(res.data.message || '上传失败');
    return res.data.data;
  };

  const loadMeta = useCallback(async () => {
    try {
      setLoadingMeta(true);
      const res = await api.get('/expense/exp-prepayment/meta');
      if (!res.data.success) return;
      const data = res.data.data || {};
      setMeta(data);
      if (isFromMatInbound) {
        if (!matInboundFormInitRef.current && variantConfig?.prefill) {
          applyMatInboundPrepaymentPrefill(form, variantConfig.prefill, data);
          matInboundFormInitRef.current = true;
        }
        return;
      }
      const ex = form.getFieldsValue(true);
      if (ex.doc_type || ex.category_id || ex.reason || ex.expense_company_id || ex.payee_ref || ex.payee_name
        || (ex.lines || []).some((l) => l?.expense_detail || l?.prepay_amount != null)) return;
      const org = data.default_budget_org_code || data.default_applicant_dept_code;
      form.setFieldsValue({
        doc_type: PREPAYMENT_DOC_TYPE,
        expense_company_id: data.default_expense_company_id ?? data.expense_company_id,
        applicant_dept_code: data.default_applicant_dept_code,
        payee_type: DEFAULT_PAYEE_TYPE,
        urgency: 'normal', expected_invoice_date: defaultExpectedInvoiceDate(),
        no_invoice_commitment_files: [], other_attachments: [],
        lines: [{ expense_detail: '', budget_org_code: org, prepay_amount: undefined }]
      });
    } catch (e) {
      message.error(e.response?.data?.message || '加载预付款表单失败');
    } finally {
      setLoadingMeta(false);
    }
  }, [form, isFromMatInbound, variantConfig]);

  const searchPayees = useCallback(async (keyword, typeOverride) => {
    const type = typeOverride || form.getFieldValue('payee_type') || DEFAULT_PAYEE_TYPE;
    const seq = ++payeeSeq.current;
    setPayeeSearching(true);
    try {
      const res = await api.get('/expense/payees/search', { params: { payee_type: type, keyword, limit: 20 } });
      if (seq === payeeSeq.current && res.data.success) setPayeeOptions(res.data.data || []);
    } catch { if (seq === payeeSeq.current) setPayeeOptions([]); }
    finally { if (seq === payeeSeq.current) setPayeeSearching(false); }
  }, [form]);

  const searchBranches = useCallback(async (keyword) => {
    const bn = form.getFieldValue('bank_name');
    if (!bn) { setBranchOptions([]); return; }
    try {
      const res = await api.get('/expense/claims/meta/bank-branches', { params: { keyword, bank_name: bn } });
      if (res.data.success) setBranchOptions((res.data.data || []).map((b) => ({ value: b.branch_name, label: b.branch_name })));
    } catch { setBranchOptions([]); }
  }, [form]);

  const clearExternalPayeeFields = useCallback(() => {
    form.setFieldsValue({
      payee_ref: undefined,
      payee_name: undefined,
      bank_name: undefined,
      bank_branch_name: undefined,
      bank_account_no: undefined,
      id_card_no: undefined,
      tax_no: undefined
    });
    setBranchOptions([]);
  }, [form]);

  const clearExternalPayeeLinkedFields = useCallback(() => {
    form.setFieldsValue({
      payee_ref: undefined,
      bank_name: undefined,
      bank_branch_name: undefined,
      bank_account_no: undefined,
      id_card_no: undefined,
      tax_no: undefined
    });
    setBranchOptions([]);
  }, [form]);

  const applyPayee = useCallback((item) => {
    if (!item) return;
    skipPayeeChangeRef.current = true;
    form.setFieldsValue({
      payee_ref: item.id,
      payee_name: item.name,
      bank_name: item.bank_name,
      bank_branch_name: item.bank_branch_name,
      bank_account_no: item.bank_account_no,
      id_card_no: item.id_card_no,
      tax_no: item.tax_no
    });
    if (item.bank_name) searchBranches('');
    queueMicrotask(() => { skipPayeeChangeRef.current = false; });
  }, [form, searchBranches]);

  const handleExternalPayeeNameChange = useCallback((value) => {
    if (skipPayeeChangeRef.current) return;
    const text = String(value ?? '').trim();
    if (!text) {
      clearExternalPayeeFields();
      return;
    }
    const matched = payees.find((item) => item.name === text);
    if (matched) {
      if (form.getFieldValue('payee_ref') !== matched.id) {
        applyPayee(matched);
      }
      return;
    }
    if (form.getFieldValue('payee_ref')) {
      clearExternalPayeeLinkedFields();
    }
  }, [applyPayee, clearExternalPayeeFields, clearExternalPayeeLinkedFields, form, payees]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const applyEmployeeSelfPayee = useCallback((defaultPayee) => {
    form.setFieldsValue({
      payee_type: 'employee',
      payee_ref: defaultPayee?.employee_no,
      payee_name: defaultPayee?.name,
      bank_name: defaultPayee?.bank_name,
      bank_branch_name: defaultPayee?.bank_branch_name || defaultPayee?.bank_name,
      bank_account_no: defaultPayee?.bank_account_no,
      id_card_no: undefined,
      tax_no: undefined
    });
  }, [form]);

  useEffect(() => {
    if (!meta || loadingMeta) return;
    const prev = prevDocType.current;
    prevDocType.current = docType;
    if (prev === docType) return;
    if (docType === LOAN_DOC_TYPE) {
      const defaultPayee = meta.default_employee_payee;
      if (!defaultPayee?.employee_no) {
        message.warning('员工档案缺少信息，无法自动填充借款收款方');
      }
      applyEmployeeSelfPayee(defaultPayee);
      return;
    }
    if (prev === LOAN_DOC_TYPE) {
      form.setFieldsValue({
        payee_type: DEFAULT_PAYEE_TYPE,
        payee_ref: undefined,
        payee_name: undefined,
        bank_name: undefined,
        bank_branch_name: undefined,
        bank_account_no: undefined,
        id_card_no: undefined,
        tax_no: undefined
      });
      setBranchOptions([]);
      searchPayees('', DEFAULT_PAYEE_TYPE);
    }
  }, [docType, meta, loadingMeta, form, applyEmployeeSelfPayee, searchPayees]);

  useEffect(() => {
    const prev = prevPayeeType.current;
    prevPayeeType.current = payeeType;
    if (isLoanApplication) return;
    if (prev == null || prev === payeeType) { searchPayees('', payeeType); return; }
    payeeSeq.current += 1;
    setPayeeOptions([]);
    clearExternalPayeeFields();
    searchPayees('', payeeType);
  }, [payeeType, clearExternalPayeeFields, searchPayees]);

  useEffect(() => {
    if (!payeeRef || payeeName) return;
    const hit = payees.find((item) => String(item.id) === String(payeeRef));
    if (hit?.name) form.setFieldValue('payee_name', hit.name);
  }, [payeeRef, payeeName, payees, form]);
  useEffect(() => {
    api.get('/expense/claims/meta/banks').then((r) => {
      if (r.data.success) setBankOptions((r.data.data || []).map((b) => ({ value: b.bank_name, label: b.bank_name })));
    }).catch(() => setBankOptions([]));
  }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/expense/exp-prepayment/commitment-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = '无票承诺书模板.docx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { message.error(e.response?.data?.message || '下载模板失败'); }
  };

  const renderFiles = (files, onRemove) => files?.length ? (
    <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
      {files.map((f, i) => (
        <div key={`${f.file_path}-${i}`} style={{ display: 'flex', gap: 8 }}>
          <span>{f.file_name}</span>
          {onRemove && <Button type="link" size="small" danger onClick={() => onRemove(i)}>删除</Button>}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <Form form={form} layout="vertical" className="expense-claim-form" style={{ opacity: loadingMeta ? 0.6 : 1 }}>
      <Section title="基本信息">
        <Row gutter={GUTTER}>
          <FieldCol>
            <Form.Item name="expense_company_id" label="费用承担公司" rules={[{ required: true, message: '请选择费用承担公司' }]}>
              <Select placeholder="选择费用承担公司" showSearch optionFilterProp="label" loading={loadingMeta}
                options={(meta?.expense_company_options || []).map((c) => ({ value: c.id, label: c.label || c.company_name }))} />
            </Form.Item>
          </FieldCol>
          <FieldCol>
            <Form.Item name="doc_type" label="单据类型" initialValue={PREPAYMENT_DOC_TYPE} rules={[{ required: true, message: '请选择单据类型' }]}>
              <Select options={PREPAYMENT_DOC_TYPE_OPTIONS} disabled={lockSourceFields} />
            </Form.Item>
          </FieldCol>
          <FieldCol>
            <Form.Item name="applicant_dept_code" label="申请人部门" rules={[{ required: true, message: '请选择申请人部门' }]}>
              <Select placeholder="选择部门" options={(meta?.applicant_dept_options || []).map((d) => ({ value: d.org_code, label: d.org_name }))} />
            </Form.Item>
          </FieldCol>
          <FieldCol>
            <Form.Item name="category_id" label="预算一级科目" rules={[{ required: true, message: '请选择预算一级科目' }]}>
              <Select placeholder="选择一级科目" showSearch optionFilterProp="label" disabled={lockBudgetSubject} onChange={() => {
                if (lockBudgetSubject) return;
                form.setFieldsValue({ lines: (form.getFieldValue('lines') || []).map((l) => ({ ...l, subitem_id: undefined })) });
              }} options={(meta?.categories || []).map((c) => ({
                value: c.id, label: `${c.name}${c.period_type_label ? `（${c.period_type_label}）` : ''}`
              }))} />
            </Form.Item>
          </FieldCol>
          <FieldCol>
            <Form.Item name="expense_occurrence_month" label="费用产生时间" rules={[
              { required: true, message: '请选择费用产生时间' },
              { validator: (_, v) => { const err = validateOccurrenceMonth(v); return err ? Promise.reject(new Error(err)) : Promise.resolve(); } }
            ]} extra={periodLabel(category?.period_type, month) ? `所属预算周期：${periodLabel(category?.period_type, month)}` : undefined}>
              <DatePicker picker="month" style={{ width: '100%' }} format="YYYY-MM"
                disabled={lockSourceFields}
                disabledDate={(c) => c && (dayjs(c).isBefore(monthBounds().min, 'month') || dayjs(c).isAfter(monthBounds().max, 'month'))} />
            </Form.Item>
          </FieldCol>
          <FieldCol>
            <Form.Item name="urgency" label="紧急程度" rules={[{ required: true, message: '请选择紧急程度' }]}>
              <Radio.Group options={URGENCY_OPTIONS} />
            </Form.Item>
          </FieldCol>
          <FieldCol span={COL2}>
            <Form.Item name="reason" label="事由" rules={[{ required: true, message: '请填写事由' }]}>
              <Input placeholder={prepaymentReasonPlaceholder(docType)} disabled={lockSourceFields} />
            </Form.Item>
          </FieldCol>
          <FieldCol span={COL2}>
            <Form.Item label="主题"><Input value={subject || '填写部门、费用产生时间与事由后自动生成'} disabled /></Form.Item>
          </FieldCol>
        </Row>
      </Section>

      <Section title="收款方">
        <Row gutter={GUTTER}>
          {isLoanApplication ? (
            <>
              <FieldCol span={FULL}>
                <Form.Item label="收款方类型">
                  <Input value="员工（本人）" disabled />
                </Form.Item>
                <Form.Item name="payee_type" hidden initialValue="employee"><Input /></Form.Item>
              </FieldCol>
              <FieldCol>
                <Form.Item name="payee_name" label="收款方名称" rules={[{ required: true, message: '员工档案缺少姓名' }]}>
                  <Input disabled />
                </Form.Item>
                <Form.Item name="payee_ref" hidden><Input /></Form.Item>
              </FieldCol>
            </>
          ) : (
            <>
              <FieldCol span={FULL}>
                <Form.Item name="payee_type" label="收款方类型" rules={[{ required: true }]}>
                  <Radio.Group options={PAYEE_TYPE_OPTIONS} disabled={false} />
                </Form.Item>
              </FieldCol>
              <FieldCol>
                <Form.Item
                  name="payee_name"
                  label="收款方名称"
                  getValueFromEvent={(value) => String(value ?? '')}
                  rules={[{ required: true, whitespace: true, message: '请填写或选择收款方名称' }]}
                >
                  <AutoComplete
                    allowClear
                    listHeight={256}
                    popupClassName="expense-payee-dropdown"
                    placeholder="输入或搜索收款方名称"
                    onSearch={(k) => searchPayees(k, payeeType)}
                    onSelect={(_, opt) => applyPayee(opt.item)}
                    onChange={handleExternalPayeeNameChange}
                    options={payees.map((p) => ({ value: p.name, label: p.name, item: p }))}
                    notFoundContent={payeeSearching ? '搜索中...' : '无匹配，可手动输入新收款方'}
                  />
                </Form.Item>
                <Form.Item name="payee_ref" hidden><Input /></Form.Item>
              </FieldCol>
            </>
          )}
          <FieldCol>
            <Form.Item name="bank_name" label="开户银行" rules={[{ required: true, message: '请填写开户银行' }]}>
              {payeeFieldsDisabled ? <Input disabled /> : (
                <Select showSearch allowClear placeholder="请选择开户银行" optionFilterProp="label" options={bankOptions}
                  onChange={(v) => { form.setFieldsValue({ bank_name: v, bank_branch_name: undefined }); setBranchOptions([]); }} />
              )}
            </Form.Item>
          </FieldCol>
          {!isLoanApplication && (
            <FieldCol>
              <Form.Item name="bank_branch_name" label="支行名称" rules={[{ required: true, message: '请选择支行名称' }]}>
                {payeeFieldsDisabled ? <Input disabled /> : (
                  <Select showSearch allowClear placeholder="请先选择开户银行，再搜索支行" filterOption={false}
                    disabled={!bankName} onSearch={searchBranches} onFocus={() => searchBranches('')} options={branchOptions} />
                )}
              </Form.Item>
            </FieldCol>
          )}
          {isLoanApplication && (
            <Form.Item name="bank_branch_name" hidden><Input /></Form.Item>
          )}
          <FieldCol>
            <Form.Item name="bank_account_no" label="银行账号" rules={[{ required: true, message: '请填写银行账号' }]}>
              <Input disabled={payeeFieldsDisabled} />
            </Form.Item>
          </FieldCol>
          {!isLoanApplication && payeeType === 'external_person' && (
            <FieldCol>
              <Form.Item name="id_card_no" label="收款人身份证号" rules={[
                { required: true, message: '外部个人须填写身份证号' },
                { validator: createPayeeFieldValidator(validateChineseIdCard) }
              ]}>
                <Input disabled={payeeFieldsDisabled} />
              </Form.Item>
            </FieldCol>
          )}
          {!isLoanApplication && payeeType === 'supplier' && (
            <FieldCol>
              <Form.Item name="tax_no" label="对方税号" rules={[
                { required: true, message: '供应商须填写税号' },
                { validator: createPayeeFieldValidator(validateSupplierTaxNo) }
              ]}>
                <Input disabled={payeeFieldsDisabled} />
              </Form.Item>
            </FieldCol>
          )}
          <FieldCol span={FULL}>
            <Form.Item
              name="no_invoice_commitment_files"
              label="无票承诺书"
              required
              initialValue={[]}
              extra={<Link onClick={downloadTemplate}><DownloadOutlined /> 下载承诺书模板（docx）</Link>}
              rules={[
                {
                  validator: async (_, value) => {
                    if (Array.isArray(value) && value.length > 0) return;
                    throw new Error('请上传无票承诺书');
                  }
                }
              ]}
            >
              <>
                <Upload beforeUpload={async (file) => {
                  try {
                    setCommitmentUploading(true);
                    const data = await uploadFile(file);
                    form.setFieldsValue({
                      no_invoice_commitment_files: [{ file_name: data.file_name || file.name, file_path: data.file_path }]
                    });
                    form.validateFields(['no_invoice_commitment_files']).catch(() => {});
                    message.success('无票承诺书已上传');
                  } catch (e) { message.error(e.response?.data?.message || e.message || '上传失败'); }
                  finally { setCommitmentUploading(false); }
                  return Upload.LIST_IGNORE;
                }} showUploadList={false} maxCount={1}>
                  <Button icon={<UploadOutlined />} loading={commitmentUploading}>上传承诺书</Button>
                </Upload>
                {renderFiles(commitmentFiles, () => {
                  form.setFieldsValue({ no_invoice_commitment_files: [] });
                  form.validateFields(['no_invoice_commitment_files']).catch(() => {});
                })}
              </>
            </Form.Item>
          </FieldCol>
          <FieldCol>
            <Form.Item name="expected_invoice_date" label="预计收票日期" rules={[{ required: true, message: '请选择预计收票日期' }]}
              initialValue={defaultExpectedInvoiceDate()}>
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabledDate={(c) => c && dayjs(c).isBefore(dayjs(), 'day')} />
            </Form.Item>
          </FieldCol>
        </Row>
      </Section>

      <Section title={prepaymentLineSectionTitle(docType)}>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              <div className="expense-line-list">
                {fields.map((field, index) => (
                  <LineCard key={field.key} field={field} index={index} form={form} canRemove={fields.length > 1}
                    onRemove={() => remove(field.name)} category={category} treeProps={treeProps} splitTreeProps={splitTreeProps}
                    categoryId={categoryId} month={month}
                    lineContentDisabled={lockLineContent}
                    subitemDisabled={lockBudgetSubject} />
                ))}
              </div>
              {!lockLineContent && (
              <Button type="primary" size="small" icon={<PlusOutlined />} className="expense-line-add-btn"
                onClick={() => add({
                  expense_detail: '',
                  budget_org_code: meta?.default_budget_org_code || meta?.default_applicant_dept_code,
                  prepay_amount: undefined
                })}>{prepaymentAddLineButtonLabel(docType)}</Button>
              )}
            </>
          )}
        </Form.List>
        <div className="expense-claim-summary">
          <div className="expense-claim-summary-item"><Text type="secondary">{prepaymentTotalLabel(docType)}</Text><Text strong>¥{prepayTotal.toFixed(2)}</Text></div>
          <div className="expense-claim-summary-item"><Text type="secondary">金额大写</Text><Text>{amountToChinese(prepayTotal) || '—'}</Text></div>
          <div className="expense-claim-summary-item"><Text type="secondary">附件张数</Text><Text strong>{otherAttachments.length + commitmentFiles.length}</Text></div>
        </div>
        <Row gutter={GUTTER}>
          <FieldCol span={FULL}>
            <Form.Item label="其他附件">
              <Upload beforeUpload={async (file) => {
                try {
                  const data = await uploadFile(file);
                  form.setFieldsValue({ other_attachments: [...(form.getFieldValue('other_attachments') || []), { file_name: data.file_name || file.name, file_path: data.file_path }] });
                  message.success('附件已上传');
                } catch (e) { message.error(e.response?.data?.message || e.message || '上传失败'); }
                return Upload.LIST_IGNORE;
              }} showUploadList={false} multiple>
                <Button icon={<UploadOutlined />}>上传附件</Button>
              </Upload>
              {renderFiles(otherAttachments, (i) => form.setFieldsValue({ other_attachments: otherAttachments.filter((_, idx) => idx !== i) }))}
            </Form.Item>
            <Form.Item name="other_attachments" hidden initialValue={[]}><Input hidden /></Form.Item>
          </FieldCol>
          <FieldCol span={FULL}><Form.Item name="remark" label="备注"><Input placeholder="选填" /></Form.Item></FieldCol>
        </Row>
      </Section>
      <Form.Item name="related_approval_no" hidden><Input /></Form.Item>
      <Form.Item name="related_approval_id" hidden><Input /></Form.Item>
      <Form.Item name="related_approval_biz_type" hidden><Input /></Form.Item>
      <Form.Item name="related_inbound_ids" hidden><Input /></Form.Item>
    </Form>
  );
}
